import React, { useMemo } from "react";
import { StyleSheet } from "react-native";

import { palette } from "../../styles";
import Block from "../Block";
import SvgIcon from "../SvgIcon";
import Touchable from "../Touchable";
import Typography from "../Typography";
import type { IconButtonProps } from "./types";

const IconButton = ({
  size,
  iconSize,
  name,
  style,
  showBadge,
  badgeColor = palette.secondary.wrongPink,
  label,
  iconColor = palette.font.secondary,
  onPress,
  disabled,
}: IconButtonProps) => {
  const containerStyle = useMemo(
    () => ({
      height: size,
      width: size,
      borderRadius: size / 2,
      ...(label
        ? {
            backgroundColor: "transparent",
            justifyContent: "space-between" as const,
          }
        : {}),
    }),
    [size, label],
  );

  const iconBox = useMemo(
    () =>
      name === "chat"
        ? {
            transform: [{ translateY: size * 0.02 }],
          }
        : {},
    [size, name],
  );

  const activeMessage = useMemo(() => {
    const activeMessageSize = size * 0.2;
    return {
      height: activeMessageSize,
      width: activeMessageSize,
      borderRadius: activeMessageSize / 2,
      top: -activeMessageSize * 0.3,
      right: -activeMessageSize * 0.3,
    };
  }, [size]);

  return (
    <Touchable
      pointerEvents={onPress ? "auto" : "none"}
      onPress={onPress}
      disabled={disabled}
      style={[styles.container, containerStyle, style]}
    >
      <Block style={iconBox}>
        <SvgIcon color={iconColor} name={name} size={iconSize ? iconSize : size * 0.4} />
        {showBadge && (
          <Block style={[styles.badge, activeMessage, { backgroundColor: badgeColor }]} />
        )}
      </Block>

      {!!label && (
        <Typography
          style={{ fontSize: 14 * (size / 52), lineHeight: 20 * (size / 52) }}
          type="secondary"
          variant="lightLabel"
        >
          {label}
        </Typography>
      )}
    </Touchable>
  );
};

export default React.memo(IconButton);

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    backgroundColor: `${palette.font.placeholder}66`,
    justifyContent: "center",
  },
  badge: {
    position: "absolute",
  },
});
